import { Routes, Route, Navigate } from "react-router-dom";
import Landing from "./routes/Landing";
import Features from "./routes/Features";
import Pricing from "./routes/Pricing";
import Blog from "./routes/Blog";
import Faq from "./routes/Faq";
import About from "./routes/About";
import Support from "./routes/Support";
import Terms from "./routes/Terms";
import Privacy from "./routes/Privacy";
import Sitemap from "./routes/Sitemap";
import Admin from "./routes/Admin";
import Login from "./routes/auth/Login";
import Signup from "./routes/auth/Signup";
import Reset from "./routes/auth/Reset";
import Confirm from "./routes/auth/Confirm";
import Dashboard from "./routes/Dashboard";
import ContentNew from "./routes/ContentNew";
import ContentEditor from "./routes/ContentEditor";
import History from "./routes/History";
import Settings from "./routes/Settings";
import Contents from "./routes/Contents";
import Personas from "./routes/Personas";
import Projects from "./routes/Projects";
import ProjectDetail from "./routes/ProjectDetail";
import Writers from "./routes/Writers";
import Billing from "./routes/Billing";
import Contact from "./routes/Contact";
import KeywordResearch from "./routes/KeywordResearch";
import NotFound from "./routes/NotFound";
import Credits from "./routes/Credits";
import { supabase } from "./lib/supabase";
import { useEffect, useState } from "react";
import Layout from "./components/Layout";
import { CreditsProvider } from "./hooks/useCredits";

function Guard({ children }: { children: JSX.Element }) {
  const [ok, setOk] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const authCandidate = (supabase as unknown as { auth?: unknown }).auth;
    if (!authCandidate || typeof authCandidate !== "object") {
      setAuthError("Service d’authentification indisponible. Réessayez dans quelques instants.");
      setOk(false);
      return;
    }

    const authRecord = authCandidate as {
      onAuthStateChange?: (cb: (event: unknown, session: unknown) => void) => {
        data?: { subscription?: { unsubscribe?: () => void } };
      };
      getSession?: () => Promise<{ data?: { session?: unknown } }>;
    };

    if (
      typeof authRecord.onAuthStateChange !== "function" ||
      typeof authRecord.getSession !== "function"
    ) {
      setAuthError("Service d’authentification indisponible. Réessayez dans quelques instants.");
      setOk(false);
      return;
    }

    setAuthError(null);
    setOk(null);

    const subscriptionResult = authRecord.onAuthStateChange((_event, session) => {
      setOk(Boolean(session));
    });

    authRecord
      .getSession()
      .then(({ data }) => {
        setOk(Boolean(data?.session));
      })
      .catch(() => {
        setAuthError(
          "Impossible de vérifier votre session. Vérifiez votre connexion puis réessayez.",
        );
        setOk(false);
      });

    return () => {
      const unsubscribe = subscriptionResult?.data?.subscription?.unsubscribe;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [retryKey]);

  if (authError) {
    return (
      <div className="mx-auto max-w-lg p-6 text-center" role="alert" aria-live="assertive">
        <h2 className="mb-2 text-lg font-semibold">Erreur de connexion</h2>
        <p className="mb-4 text-sm text-muted">{authError}</p>
        <button
          type="button"
          className="rounded bg-accent px-4 py-2 text-sm text-white hover:opacity-90"
          onClick={() => setRetryKey((v) => v + 1)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (ok === null) return <div className="p-6">Loading…</div>;

  return ok ? (
    <CreditsProvider>
      <Layout>{children}</Layout>
    </CreditsProvider>
  ) : (
    <Navigate to="/auth/login" replace />
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/about" element={<About />} />
      <Route path="/support" element={<Support />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/sitemap" element={<Sitemap />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/signup" element={<Signup />} />
      <Route path="/auth/reset" element={<Reset />} />
      <Route path="/auth/confirm" element={<Confirm />} />
      <Route
        path="/dashboard"
        element={
          <Guard>
            <Dashboard />
          </Guard>
        }
      />
      <Route
        path="/content/new"
        element={
          <Guard>
            <ContentNew />
          </Guard>
        }
      />
      <Route
        path="/content/:id"
        element={
          <Guard>
            <ContentEditor />
          </Guard>
        }
      />
      <Route
        path="/contents"
        element={
          <Guard>
            <Contents />
          </Guard>
        }
      />
      <Route
        path="/keywords"
        element={
          <Guard>
            <KeywordResearch />
          </Guard>
        }
      />
      <Route
        path="/personas"
        element={
          <Guard>
            <Personas />
          </Guard>
        }
      />
      <Route
        path="/projects"
        element={
          <Guard>
            <Projects />
          </Guard>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <Guard>
            <ProjectDetail />
          </Guard>
        }
      />
      <Route
        path="/writers"
        element={
          <Guard>
            <Writers />
          </Guard>
        }
      />
      <Route
        path="/credits"
        element={
          <Guard>
            <Credits />
          </Guard>
        }
      />
      <Route
        path="/billing"
        element={
          <Guard>
            <Billing />
          </Guard>
        }
      />
      <Route
        path="/contact"
        element={<Contact />}
      />
      <Route
        path="/history"
        element={
          <Guard>
            <History />
          </Guard>
        }
      />
      <Route
        path="/settings"
        element={
          <Guard>
            <Settings />
          </Guard>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
